import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatToUSDate } from 'utils/dateTimeUtils/dateFormatUtil';
import { calcDurationMinutes } from 'utils/dateTimeUtils/timeUtil';

import {
    CONTINUED_HEADER_Y,
    DATE_COLUMN_WIDTH,
    DURATION_COLUMN_WIDTH,
    FONT_SIZE_SUBTITLE,
    FONT_SIZE_TABLE,
    FONT_SIZE_TITLE,
    GROUP_HEADER_FILL_COLOR,
    GROUP_HEADER_TEXT_COLOR,
    HEADER_LINE_HEIGHT,
    HEADER_MARGIN_TOP,
    HEADER_TITLE_SPACING,
    LINE_COLOR,
    MARGIN_LEFT,
    MINUTES_PER_HOUR,
    TABLE_MARGIN_TOP,
    TYPE_COLUMN_WIDTH
} from './constants';
import type { FlightResponse, GenerateFlightsPdfParams } from './types';

const formatDuration = (totalMinutes: number): string => {
    const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
    const minutes = Math.round(totalMinutes % MINUTES_PER_HOUR);

    if (hours === 0) {
        return `${minutes}m`;
    }

    return `${hours}h ${minutes}m`;
};

const formatPilotName = (flight: FlightResponse): string => {
    const { pilot } = flight;

    if (!pilot) {
        return '';
    }

    return `${pilot.firstName ?? ''} ${pilot.lastName ?? ''}`.trim();
};

const formatLocation = (flight: FlightResponse): string => {
    const { city, state } = flight;

    if (city && state) {
        return `${city}, ${state}`;
    }

    return city || state || '';
};

const groupFlightsByApplicationType = (
    flights: FlightResponse[]
): Record<string, FlightResponse[]> =>
    flights.reduce<Record<string, FlightResponse[]>>((groups, flight) => {
        const key = flight.applicationType;
        const existing = groups[key] ?? [];

        return { ...groups, [key]: [...existing, flight] };
    }, {});

const generateFlightsPdf = ({
    applicationTypeLabels,
    flights,
    generatedDate,
    totalFlightCount
}: GenerateFlightsPdfParams): Blob => {
    const pdfDocument = new jsPDF();
    const pageWidth = pdfDocument.internal.pageSize.getWidth();
    let cursorY = HEADER_MARGIN_TOP;

    pdfDocument.setFontSize(FONT_SIZE_TITLE);
    pdfDocument.setFont('helvetica', 'bold');
    pdfDocument.text('Flight Report', MARGIN_LEFT, cursorY);
    cursorY += HEADER_LINE_HEIGHT + HEADER_TITLE_SPACING;

    pdfDocument.setFontSize(FONT_SIZE_SUBTITLE);
    pdfDocument.setFont('helvetica', 'normal');
    pdfDocument.text(`Generated: ${generatedDate}`, MARGIN_LEFT, cursorY);
    cursorY += HEADER_LINE_HEIGHT;
    pdfDocument.text(`Total Flights: ${totalFlightCount}`, MARGIN_LEFT, cursorY);
    cursorY += HEADER_LINE_HEIGHT;

    pdfDocument.setDrawColor(...LINE_COLOR);
    pdfDocument.line(MARGIN_LEFT, cursorY, pageWidth - MARGIN_LEFT, cursorY);
    cursorY += TABLE_MARGIN_TOP;

    const groups = groupFlightsByApplicationType(flights);
    const columnHeaders = ['Date', 'Duration', 'Pilot', 'Type', 'Location'];

    Object.entries(groups).forEach(([applicationType, groupFlights]) => {
        const label = applicationTypeLabels[applicationType] ?? applicationType;
        const totalMinutes = groupFlights.reduce(
            (total, flight) => total + calcDurationMinutes(flight.startTime, flight.endTime),
            0
        );
        const flightLabel = groupFlights.length === 1 ? 'flight' : 'flights';
        const groupSummary = `${label} - ${groupFlights.length} ${flightLabel} - ${formatDuration(totalMinutes)} total`;

        const bodyRows = groupFlights.map(flight => {
            const duration = calcDurationMinutes(flight.startTime, flight.endTime);

            return [
                formatToUSDate(new Date(flight.startTime)),
                formatDuration(duration),
                formatPilotName(flight),
                label,
                formatLocation(flight)
            ];
        });

        autoTable(pdfDocument, {
            body: bodyRows,
            bodyStyles: { fontSize: FONT_SIZE_TABLE },
            columnStyles: {
                0: { cellWidth: DATE_COLUMN_WIDTH },
                1: { cellWidth: DURATION_COLUMN_WIDTH },
                3: { cellWidth: TYPE_COLUMN_WIDTH }
            },
            didDrawPage: data => {
                if (data.pageNumber > 1) {
                    pdfDocument.setFontSize(FONT_SIZE_TABLE);
                    pdfDocument.setFont('helvetica', 'italic');
                    pdfDocument.text('Flight Report (continued)', MARGIN_LEFT, CONTINUED_HEADER_Y);
                }
            },
            head: [
                [
                    {
                        colSpan: columnHeaders.length,
                        content: groupSummary,
                        styles: {
                            fillColor: GROUP_HEADER_FILL_COLOR,
                            fontStyle: 'bold',
                            textColor: GROUP_HEADER_TEXT_COLOR
                        }
                    }
                ],
                columnHeaders
            ],
            headStyles: { fontSize: FONT_SIZE_TABLE },
            margin: { left: MARGIN_LEFT, right: MARGIN_LEFT },
            startY: cursorY,
            theme: 'grid'
        });

        const lastTable = (pdfDocument as unknown as { lastAutoTable?: { finalY?: number } })
            .lastAutoTable;

        cursorY = (lastTable?.finalY ?? cursorY) + TABLE_MARGIN_TOP;
    });

    return pdfDocument.output('blob');
};

export default generateFlightsPdf;
